import React from 'react';
import { useTranslation } from 'next-i18next';
import { X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@lb-classroom/ui';
import type { LanguageAbilityTableModalProps } from './language-ability-table-modal.types';

interface TableThProps {
  children: React.ReactNode;
}

interface TableTdProps {
  children: React.ReactNode;
  rowSpan?: number;
}

const TableTh = ({ children }: TableThProps) => {
  return (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      {children}
    </th>
  );
};

const TableTd = ({ children, rowSpan }: TableTdProps) => {
  return (
    <td
      rowSpan={rowSpan}
      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
    >
      {children}
    </td>
  );
};

export const LanguageAbilityTableModal = ({
  onClose,
}: LanguageAbilityTableModalProps) => {
  const { t } = useTranslation('common');

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('modals.languageAbilityTable.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={onClose}>
            <span className="sr-only">{t('buttons.close')}</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <div className="">
            <div className="ability-comparison-table">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <TableTh>LingualBox</TableTh>
                    <TableTh>TOEFL iBT</TableTh>
                    <TableTh>IELTS</TableTh>
                    <TableTh>CEFR</TableTh>
                    <TableTh>TOEIC</TableTh>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  <tr className="">
                    <TableTd>Beginner</TableTd>
                    <TableTd rowSpan={3}>0-31</TableTd>
                    <TableTd rowSpan={3}>0-4</TableTd>
                    <TableTd>A1</TableTd>
                    <TableTd>120-224</TableTd>
                  </tr>
                  <tr>
                    <TableTd>High Beginner</TableTd>
                    <TableTd rowSpan={2}>A2</TableTd>
                    <TableTd rowSpan={2}>225-549</TableTd>
                  </tr>
                  <tr>
                    <TableTd>Low Intermediate</TableTd>
                  </tr>
                  <tr>
                    <TableTd rowSpan={2}>Intermediate</TableTd>
                    <TableTd>32-34</TableTd>
                    <TableTd>4.5</TableTd>
                    <TableTd rowSpan={2}>B1</TableTd>
                    <TableTd rowSpan={2}>550-784</TableTd>
                  </tr>
                  <tr>
                    <TableTd>35-45</TableTd>
                    <TableTd>5</TableTd>
                  </tr>
                  <tr>
                    <TableTd rowSpan={2}>Upper Intermediate</TableTd>
                    <TableTd>46-59</TableTd>
                    <TableTd>5.5</TableTd>
                    <TableTd rowSpan={3}>B2</TableTd>
                    <TableTd rowSpan={3}>785-944</TableTd>
                  </tr>
                  <tr>
                    <TableTd>60-78</TableTd>
                    <TableTd>6</TableTd>
                  </tr>
                  <tr>
                    <TableTd rowSpan={2}>Low Advanced</TableTd>
                    <TableTd>78-93</TableTd>
                    <TableTd>6.5</TableTd>
                  </tr>
                  <tr>
                    <TableTd>94-101</TableTd>
                    <TableTd>7</TableTd>
                    <TableTd rowSpan={3}>C1</TableTd>
                    <TableTd rowSpan={5}>945-</TableTd>
                  </tr>
                  <tr>
                    <TableTd rowSpan={4}>Advanced</TableTd>
                    <TableTd>102-109</TableTd>
                    <TableTd>7.5</TableTd>
                  </tr>
                  <tr>
                    <TableTd>110-114</TableTd>
                    <TableTd>8</TableTd>
                  </tr>
                  <tr>
                    <TableTd>115-120</TableTd>
                    <TableTd>8.5-9</TableTd>
                    <TableTd>C2</TableTd>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="level-explanations mt-8">
              <ul>
                <li>
                  <b>Beginner: </b>You understand some words but can{"'"}t
                  communicate beyond the most common words or phrases.
                </li>
                <li>
                  <b>High Beginner: </b>You can use and understand very simple
                  language in familiar and common situations.
                </li>
                <li>
                  <b>Low Intermediate: </b>You can communicate in simple and
                  routine tasks but your vocabulary and grammar is limited.
                </li>
                <li>
                  <b>Intermediate: </b>You can have functional communication
                  with native speakers in familiar contexts but have difficulty
                  in new situations.
                </li>
                <li>
                  <b>High Intermediate: </b>You can communicate with and
                  understand native speakers in everyday situations with little
                  miscommunication but still make occasional mistakes.
                </li>
                <li>
                  <b>Low Advanced: </b>You can interact with native speakers in
                  a wide variety of situations and express yourself with
                  grammatical accuracy and a broad range of vocabulary.
                </li>
                <li>
                  <b>Advanced: </b>You can easily and accurately communicate
                  with native speakers in any situation and on any subject.
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
